// frontend/src/pages/dispatch/ManualShipping.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Grid, Card, CardContent, Typography, Button, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, LinearProgress, TextField, Divider, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Checkbox, FormControlLabel
} from '@mui/material';
import {
  ArrowBack, LocalShipping, CheckCircle, Person, Save
} from '@mui/icons-material';
import { manufacturingAPI } from '../../services/api';
import { toast } from 'react-toastify';
import usePageTitle from '../../hooks/usePageTitle';

export default function ManualShipping() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  
  // Form state
  const [shipmentNote, setShipmentNote] = useState('');
  const [collectorName, setCollectorName] = useState('');
  const [selectedLines, setSelectedLines] = useState({});

  usePageTitle(order ? `Order: ${order.order_number}` : null);

  useEffect(() => {
    loadOrderDetails();
  }, [orderId]);

  const loadOrderDetails = async () => {
    try {
      const response = await manufacturingAPI.customerOrders.get(orderId);
      setOrder(response.data);
      
      // Initialize selected lines with all shippable items
      const initialSelected = {};
      response.data.lines?.forEach(line => {
        const toShip = line.quantity_allocated || 0;
        if (toShip > 0) {
          initialSelected[line.id] = toShip;
        }
      });
      setSelectedLines(initialSelected);
    } catch (error) {
      console.error('Failed to load order details:', error);
      toast.error('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (lineId, maxQty) => (event) => {
    const value = Math.min(Math.max(0, parseInt(event.target.value) || 0), maxQty);
    setSelectedLines(prev => ({
      ...prev,
      [lineId]: value
    }));
  };

  const handleConfirmShipment = async () => {
    setProcessing(true);
    setConfirmDialog(false);

    let successCount = 0;
    let errorCount = 0;

    for (const [lineId, quantity] of Object.entries(selectedLines)) {
      if (quantity > 0) {
        try {
          await manufacturingAPI.customerOrders.shipQuantity(orderId, {
            line_id: parseInt(lineId),
            quantity: quantity
          });
          successCount++;
        } catch (error) {
          errorCount++;
          console.error(`Failed to ship line ${lineId}:`, error);
        }
      }
    }

    setProcessing(false);

    if (successCount > 0) {
      toast.success(`Successfully shipped ${successCount} line(s)`, { autoClose: 5000 });
    }
    if (errorCount > 0) {
      toast.error(`Failed to ship ${errorCount} line(s)`);
    }

    // Reload and potentially redirect
    await loadOrderDetails();
    
    // Check if order is now complete
    const response = await manufacturingAPI.customerOrders.get(orderId);
    if (response.data.status === 'complete') {
      toast.success('Order is now complete!');
      navigate('/dispatch');
    }
  };

  const getTotalToShip = () => {
    return Object.values(selectedLines).reduce((sum, qty) => sum + qty, 0);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Loading...</Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (!order) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Order not found</Alert>
        <Button onClick={() => navigate('/dispatch')} sx={{ mt: 2 }}>
          Back to Dispatch
        </Button>
      </Box>
    );
  }

  const shippableLines = order.lines?.filter(line => {
    return (line.quantity_allocated || 0) > 0;
  }) || [];

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button 
          startIcon={<ArrowBack />} 
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back to Shipping Methods
        </Button>
        
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Person color="primary" />
          Manual Shipment / Collection
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          Order: <strong>{order.order_number}</strong> | Customer: <strong>{order.customer_name}</strong>
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Select Items to Ship</Typography>
              <Divider sx={{ mb: 2 }} />

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Available</TableCell>
                      <TableCell align="right">Quantity to Ship</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shippableLines.map((line) => {
                      const maxToShip = line.quantity_allocated || 0;
                      
                      return (
                        <TableRow key={line.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {line.product_display}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {line.product_name}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip label={maxToShip} size="small" color="success" />
                          </TableCell>
                          <TableCell align="right" sx={{ width: 150 }}>
                            <TextField
                              type="number"
                              size="small"
                              value={selectedLines[line.id] || 0}
                              onChange={handleQuantityChange(line.id, maxToShip)}
                              inputProps={{ min: 0, max: maxToShip }}
                              sx={{ width: 100 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Shipment Details */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Shipment Details</Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Collector Name (if collection)"
                    value={collectorName}
                    onChange={(e) => setCollectorName(e.target.value)}
                    placeholder="Enter name of person collecting"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Shipment Notes"
                    value={shipmentNote}
                    onChange={(e) => setShipmentNote(e.target.value)}
                    placeholder="Any notes about this shipment..."
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Summary Sidebar */}
        <Grid item xs={12} md={4}>
          <Card sx={{ position: 'sticky', top: 20 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Shipment Summary</Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Total Items to Ship</Typography>
                <Typography variant="h3" color="primary">
                  {getTotalToShip()}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Delivery To</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {order.delivery_name || order.customer_name}
                </Typography>
                {order.delivery_postcode && (
                  <Typography variant="body2">{order.delivery_postcode}</Typography>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Button
                fullWidth
                variant="contained"
                color="success"
                size="large"
                startIcon={<CheckCircle />}
                onClick={() => setConfirmDialog(true)}
                disabled={getTotalToShip() === 0 || processing}
              >
                {processing ? 'Processing...' : 'Confirm Shipment'}
              </Button>

              <Alert severity="info" sx={{ mt: 2 }}>
                This will mark items as shipped and reduce allocated stock.
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>Confirm Manual Shipment</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            You are about to ship <strong>{getTotalToShip()} items</strong> for order <strong>{order.order_number}</strong>.
          </Typography>
          
          {collectorName && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Collected by: <strong>{collectorName}</strong>
            </Typography>
          )}
          
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone. Stock will be deducted from inventory.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="success" 
            onClick={handleConfirmShipment}
            startIcon={<LocalShipping />}
          >
            Confirm Shipment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}